import { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import useWhichReverseGeocodeService from '@rsos/hooks/useWhichReverseGeocodeService';
import { reverseGeocodeLocationToAddress } from '@rsos/maps/interface/reverseGeocode/helpers';
import { selectGeocodeService } from '@rsos/reduxDataStore';
import { WORLD } from '@rsos/reduxDataStore/src/features/gisSearch/constants';
import {
  isValidLatitude,
  isValidLongitude,
} from '@rsos/utils/isValidCoordinates';
import type { PlaceCandidate } from './GlobalMapSearch.types';

export interface LatLongPoint {
  latitude: number;
  longitude: number;
}

interface ReverseGeocodeResponse {
  address?:
    | string
    | {
        Match_addr?: string;
        LongLabel?: string;
        ShortLabel?: string;
        PlaceName?: string;
        Address?: string;
        City?: string;
        MetroArea?: string;
        Region?: string;
        RegionAbbr?: string;
        Postal?: string;
        CntryName?: string;
      };
  rgAddressProvider?: string;
  PlaceName?: string;
  StAddr?: string;
  SubAddr?: string;
  City?: string;
  RegionAbbr?: string;
  Postal?: string;
  MetroArea?: string;
  Region?: string;
  Zone?: string;
  LongLabel?: string;
  Addr_type?: string;
  Country?: string;
  addressObject?: {
    home?: string;
    street?: string;
    city?: string;
    region?: string;
    postal_code?: string;
    country?: string;
  };
}

const reverseGeocode = reverseGeocodeLocationToAddress as unknown as (args: {
  latitude: number;
  longitude: number;
  service?: unknown;
  serviceUrl?: string;
  token?: string | null;
}) => Promise<ReverseGeocodeResponse | null>;

function pointKey(point: LatLongPoint): string {
  return `${point.latitude},${point.longitude}`;
}

export function parseLatLongTerm(term: string): LatLongPoint | null {
  const trimmed = term.trim();
  if (!trimmed || !trimmed.includes(',')) return null;

  const parts = trimmed.split(',').map(part => part.trim());
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null;

  if (!isValidLatitude(parts[0]) || !isValidLongitude(parts[1])) return null;

  const latitude = Number(parts[0]);
  const longitude = Number(parts[1]);

  return { latitude, longitude };
}

/**
 * Build a `PlaceCandidate` so the hit renders as an ordinary place row
 * in the dropdown and pins identically on click. Uses the reverse-
 * geocoded label when available, otherwise the raw coordinates.
 */
function toPlaceCandidate(
  point: LatLongPoint,
  response: ReverseGeocodeResponse | null,
): PlaceCandidate {
  const nestedAddress =
    response?.address && typeof response.address === 'object'
      ? response.address
      : {};
  const addressObject = response?.addressObject ?? {};
  const responseAddressLabel =
    typeof response?.address === 'string' ? response.address : '';
  const coordLabel = `${point.latitude}, ${point.longitude}`;
  const label =
    response?.LongLabel ||
    nestedAddress.LongLabel ||
    responseAddressLabel ||
    coordLabel;
  const provider = response?.rgAddressProvider?.toLowerCase();
  const locName = provider === 'gis' ? 'Local' : WORLD;
  const placeName =
    response?.PlaceName || nestedAddress.PlaceName || addressObject.home || '';
  const streetAddress =
    response?.StAddr ||
    nestedAddress.Address ||
    nestedAddress.ShortLabel ||
    addressObject.street ||
    addressObject.home ||
    '';
  const city = response?.City || nestedAddress.City || addressObject.city || '';
  const region =
    response?.Region ||
    nestedAddress.Region ||
    nestedAddress.RegionAbbr ||
    addressObject.region ||
    '';
  const postal =
    response?.Postal || nestedAddress.Postal || addressObject.postal_code || '';

  // Prefer a human address as the primary title so the pinned popover
  // shows the street / label above the lat/lng line — not just coords.
  const primaryTitle = placeName || streetAddress || label || coordLabel;

  return {
    address: label,
    location: { x: point.longitude, y: point.latitude },
    attributes: {
      PlaceName: primaryTitle,
      // Avoid duplicating the primary title on the secondary line when
      // PlaceName fell back to the street address.
      StAddr:
        streetAddress && streetAddress !== primaryTitle ? streetAddress : '',
      SubAddr: response?.SubAddr || '',
      City: city,
      RegionAbbr:
        response?.RegionAbbr ||
        nestedAddress.RegionAbbr ||
        addressObject.region ||
        region,
      Postal: postal,
      MetroArea: response?.MetroArea || nestedAddress.MetroArea || city,
      Region: region,
      Zone: response?.Zone || '',
      LongLabel: label,
      ShortLabel: coordLabel,
      Addr_type: response?.Addr_type || 'lat_long',
      Loc_name: locName,
      Country:
        response?.Country ||
        nestedAddress.CntryName ||
        addressObject.country ||
        '',
    },
  };
}

export interface ResolveLatLongPlaceArgs {
  point: LatLongPoint;
  service?: unknown;
  serviceUrl?: string;
  token?: string | null;
}

/**
 * Reverse-geocode a lat/long point into a `PlaceCandidate` suitable for
 * dropdown suggestion + pinned popover (address fields + coordinates).
 * Falls back to a coords-only candidate if reverse geocode fails.
 */
export async function resolveLatLongPlaceCandidate({
  point,
  service,
  serviceUrl,
  token,
}: ResolveLatLongPlaceArgs): Promise<PlaceCandidate> {
  try {
    const response = await reverseGeocode({
      latitude: point.latitude,
      longitude: point.longitude,
      service,
      serviceUrl,
      token: token || null,
    });
    return toPlaceCandidate(point, response);
  } catch {
    return toPlaceCandidate(point, null);
  }
}

/**
 * Background-lookup state for the most recent lat/long term. `success`
 * carries the resolved place (with reverse-geocoded label when available).
 */
export type LatLongLookupState =
  | { status: 'pending'; key: string }
  | { status: 'success'; key: string; place: PlaceCandidate };

interface UseLatLongSearch {
  /** See {@link parseLatLongTerm}. */
  parse: (term: string) => LatLongPoint | null;
  /**
   * Latest lookup state. Consumers inject a dropdown row only when
   * `status === 'success'` and surface the pending flag in the combined
   * "searching" indicator.
   */
  lookup: LatLongLookupState | null;
  /**
   * Fire a background reverse-geocode for the given (already-parsed)
   * point. Does NOT teleport the map — the result surfaces as a
   * dropdown row the user clicks to pin, like any other place.
   * Idempotent for a point that's already pending/resolved.
   */
  kickoff: (point: LatLongPoint) => void;
  /** Clear the lookup so its row disappears (e.g. input edited away). */
  reset: () => void;
}

/**
 * Background-fetch wrapper around lat/long reverse geocoding. Modeled
 * on `useWhat3WordsSearch`: a kickoff stamps a fresh request, the async
 * fetch resolves into local state, and stale responses are dropped via
 * a monotonic request id so a fast follow-up wins.
 */
export default function useLatLongSearch(): UseLatLongSearch {
  const [lookup, setLookup] = useState<LatLongLookupState | null>(null);
  const requestIdRef = useRef(0);
  const activeKeyRef = useRef<string | null>(null);
  const service = useWhichReverseGeocodeService();
  const geocodeService = useSelector(selectGeocodeService) as
    | {
        gis_service_url?: string;
        token?: string | null;
      }
    | undefined;

  const kickoff = useCallback(
    (point: LatLongPoint) => {
      const key = pointKey(point);
      // Already chasing (or done with) this exact point — don't re-fetch
      // on every debounced keystroke for an unchanged lat/long term.
      if (activeKeyRef.current === key) return;
      activeKeyRef.current = key;
      const requestId = ++requestIdRef.current;
      setLookup({ status: 'pending', key });
      void (async () => {
        const place = await resolveLatLongPlaceCandidate({
          point,
          service,
          serviceUrl: geocodeService?.gis_service_url,
          token: geocodeService?.token || null,
        });
        if (requestIdRef.current !== requestId) return;
        setLookup({ status: 'success', key, place });
      })();
    },
    [service, geocodeService],
  );

  const reset = useCallback(() => {
    // Bump the request id so any in-flight fetch is ignored on return.
    requestIdRef.current += 1;
    activeKeyRef.current = null;
    setLookup(null);
  }, []);

  return useMemo(
    () => ({ parse: parseLatLongTerm, lookup, kickoff, reset }),
    [lookup, kickoff, reset],
  );
}
